/** Append-only task return dates in the existing encrypted execution map.
 * Independent return receipts cannot overwrite a concurrent reschedule.
 * Execution entries, work marks and node planning fields are never removed.
 */
import { USER_ORIGIN } from './origins.js';
import { validDay } from '../core/recurrence.js';
import { t } from '../../shared/i18n/index.js';
const PREFIX = 'task-postponement:';
const RETURN = 'task-return:';
const caches = new WeakMap();
const text = value => typeof value === 'string' && value.length > 0 && value.length <= 2000;
function recordKey(record) {
  if (!record || !text(record.id)) return null;
  if (record.kind === 'postpone' && text(record.nodeId) &&
      (record.until === null || validDay(record.until)) &&
      typeof record.at === 'string' && Number.isFinite(Date.parse(record.at))) {
    return PREFIX + encodeURIComponent(JSON.stringify(record.id));
  }
  if (record.kind === 'return' && text(record.targetNoteId)) {
    return RETURN + encodeURIComponent(JSON.stringify([record.id, record.targetNoteId]));
  }
  return null;
}
export function readPostponementEvents(dm) {
  if (!dm) return [];
  const out = [];
  dm.doc.getMap('execution').forEach((value, key) => {
    if ((key.startsWith(PREFIX) || key.startsWith(RETURN)) && recordKey(value) === key) out.push(value);
  });
  return out;
}
export function importPostponementEvents(dm, records) {
  if (!Array.isArray(records)) throw new Error('Invalid task postponements');
  const map = dm.doc.getMap('execution');
  const cache = caches.get(dm.doc);
  if (cache) cache.value = null;
  dm.doc.transact(() => {
    for (const record of records) {
      const key = recordKey(record);
      if (key && !map.has(key)) map.set(key, record.kind === 'postpone'
        ? {kind: 'postpone', id: record.id, nodeId: record.nodeId, until: record.until, at: record.at}
        : {kind: 'return', id: record.id, targetNoteId: record.targetNoteId});
    }
  }, USER_ORIGIN);
}
export function pendingPostponements(dm) {
  if (!dm) return new Map();
  let cache = caches.get(dm.doc);
  if (!cache) {
    cache = { value: null };
    caches.set(dm.doc, cache);
    dm.doc.getMap('execution').observe(() => { cache.value = null; });
  }
  if (cache.value) return cache.value;
  const latest = new Map(), returned = new Set();
  for (const event of readPostponementEvents(dm)) {
    if (event.kind === 'return') { returned.add(event.id); continue; }
    const previous = latest.get(event.nodeId);
    if (!previous || Date.parse(event.at) > Date.parse(previous.at) || (Date.parse(event.at) === Date.parse(previous.at) && event.id > previous.id)) latest.set(event.nodeId, event);
  }
  cache.value = new Map([...latest].filter(([, event]) => event.until && !returned.has(event.id)));
  return cache.value;
}
export function writePostponement(dm, nodeId, until, id, at = new Date().toISOString()) {
  // Preserve causal order even when two choices share a millisecond or the
  // device clock moved backwards since the last observed choice.
  const latest = readPostponementEvents(dm).filter(e => e.kind === 'postpone' && e.nodeId === nodeId)
    .reduce((value, e) => Math.max(value, Date.parse(e.at)), 0);
  at = new Date(Math.max(Date.parse(at), latest + 1)).toISOString();
  const event = { kind: 'postpone', id, nodeId, until, at };
  if (!recordKey(event)) throw new Error(t('routines.error.returnDate'));
  importPostponementEvents(dm, [event]);
  return event;
}
export function recordTaskReturn(dm, event, targetNoteId) {
  importPostponementEvents(dm, [{ kind: 'return', id: event.id, targetNoteId }]);
}
export function isPostponed(item, pending, today) {
  return !!pending.get(item.nodeId)?.until && pending.get(item.nodeId).until > today;
}
